import Grid from "@mui/material/Grid";
import CCOContainer from "../../fundamentals/CCOContainer";
import C091EasyLangTeaser from "./C091EasyLangTeaser";
import useWindowSize from "../../../utility/useWindowSize";
import {NextRouter, useRouter} from "next/router";
import C091EasyLangSubTeaser from "./C091EasyLangSubTeaser";
import {Crops} from "../../../../packages/graphql-layer/src/__generated__/crops";

export interface NavigationProps {
    navigationRoot: any
}

const DESKTOP_TEASER_GAP = 16;
const DESKTOP_TEASER_MAX_WIDTH = 228;
const EXTRA_LARGE_GRID_CONTAINER_MAX_WIDTH = '1496px';

export function isActive(item: any, router: NextRouter) {
    const asPath = router?.asPath || "/";
    const path = asPath.indexOf('?') > -1 ?
        asPath.substring(0, asPath.indexOf('?')) :
        asPath;
    const coremId = item?.id;
    const selfUrl = item?.selfUrl;
    if (path && path.split('/').length === 2) {
        return path === selfUrl;
    }
    return (selfUrl && path.indexOf(selfUrl) > -1 && selfUrl.split('/').length > 2) ||
        (path.indexOf('/preview') > -1 && path.indexOf(coremId) > -1);
}

function findDeepestActiveItem(items: any[] | undefined, router: NextRouter): any | undefined {
    if (!items || items.length === 0) {
        return undefined;
    }

    for (const item of items) {
        const activeChild = findDeepestActiveItem(item.children, router);
        if (activeChild) {
            return activeChild;
        }
        if (isActive(item, router)) {
            return item;
        }
    }

    return undefined;
}

export function getHeight(lowerThanWindowLarge: boolean,
                          lowerThanWindowSmall: boolean,
                          isWindowMedium: boolean) {
    let height: string = '209px';
    if (lowerThanWindowLarge) {
        height = '391px';
    }
    if (isWindowMedium) {
        height = '519px';
    }
    if (lowerThanWindowSmall) {
        height = 'auto';
    }
    return height;
}

export function getPicHeight(lowerThanWindowLarge: boolean,
                             lowerThanWindowSmall: boolean,
                             isWindowMedium: boolean) {
    let height: string = '137px';
    if (lowerThanWindowLarge) {
        height = '319px';
    }
    if (isWindowMedium) {
        height = '447px';
    }
    if (lowerThanWindowSmall) {
        height = 'auto';
    }
    return height;
}

export function getImageDimensions(lowerThanWindowLarge: boolean) {
    const dimensions = lowerThanWindowLarge
        ? Crops.landscape_ratio16x9.w1272h716
        : Crops.landscape_ratio16x9.w585h329;

    return {
        width: dimensions.width.toString(),
        height: dimensions.height.toString()
    };
}

function C091EasyLangContainer(props: NavigationProps) {
    const router = useRouter();
    const MAX_ITEMS = 6;
    const {
        greaterThanWindowLarge,
        greaterThanWindowExtraLarge,
        isWindowExtraLarge,
        isWindowMedium,
        lowerThanWindowLarge,
        lowerThanWindowSmall
    } = useWindowSize();
    const padding = greaterThanWindowExtraLarge
        ? '24px'
        : isWindowMedium
            ? 'clamp(22.5px, calc(20.6215px + 0.226872vw), 23.015px)'
            : '16px';
    const children = props.navigationRoot?.children?.slice(0, MAX_ITEMS - 1) || [];
    const displayedItemsCount = 1 + children.length;
    const gridContainerMaxWidth = isWindowExtraLarge ? EXTRA_LARGE_GRID_CONTAINER_MAX_WIDTH : undefined;

    const height = getHeight(lowerThanWindowLarge, lowerThanWindowSmall, isWindowMedium);
    const picHeight = getPicHeight(lowerThanWindowLarge, lowerThanWindowSmall, isWindowMedium);
    const imageDimensions = getImageDimensions(lowerThanWindowLarge);
    const desktopGapWidth = Math.max(0, displayedItemsCount - 1) * DESKTOP_TEASER_GAP;
    const desktopGridColumnWidth = `minmax(0, min(${DESKTOP_TEASER_MAX_WIDTH}px, calc((100% - ${desktopGapWidth}px) / ${displayedItemsCount})))`;
    const textHeight = lowerThanWindowSmall
        ? '71px'
        : '72px';
    const activeItem = getActiveItem(children, router.asPath);
    const activeMobileItem = findDeepestActiveItem(children, router) ?? activeItem;

    function renderGridItem(item: any | undefined,
                            key: string,
                            router: NextRouter,
                            activeOverride?: boolean) {
        const active = activeOverride ?? (isActive(item, router) || item === activeItem);
        return (greaterThanWindowLarge || active) && <Grid size={{xs: 12, sm: 12, md: 12, lg: 2}} key={key}>
            <div style={{
                height: height,
                width: '100%'
            }} id={item?.id}>
                {item && <C091EasyLangTeaser
                    navItem={item}
                    height={height}
                    picHeight={picHeight}
                    imageAspectRatio={lowerThanWindowSmall ? '16 / 9' : undefined}
                    imageDimensions={imageDimensions}
                    textHeight={textHeight}
                    isActive={active}
                />}
            </div>
        </Grid>
    }

    function renderGridSubItem(item: any | undefined, key: string, router: NextRouter) {
        const active = isActive(item, router);
        const height = '74px';
        return <Grid size={{xs: 12, sm: 4, md: 4, lg: 2}} key={key}>
            <div style={{height: height, width: '100%'}} id={item?.id}>
                {item && <C091EasyLangSubTeaser
                    navItem={item}
                    height={height}
                    isActive={active}
                />}
            </div>
        </Grid>
    }

    function renderSecondaryNavigation(item: any, router: NextRouter) {
        if (!item?.children?.length || item.id === props.navigationRoot.id) return null;
        if (!greaterThanWindowLarge) return null;

        const subItems = item.children.slice(0, displayedItemsCount);

        return <CCOContainer width={"full"} background={'#DCDCDC'} gridContainerMaxWidth={gridContainerMaxWidth}>
            <Grid id={"subNaviGrid"} container spacing={greaterThanWindowLarge ? 0 : 4} size={{xs: 12}}
                  sx={{
                      px: padding,
                      py: 4,
                      ...(greaterThanWindowLarge ? {
                          display: 'grid',
                          gridTemplateColumns: `repeat(${displayedItemsCount}, ${desktopGridColumnWidth})`,
                          columnGap: `${DESKTOP_TEASER_GAP}px`,
                          justifyContent: 'center',
                          '& > .MuiGrid-root': {
                              flexBasis: 'auto',
                              maxWidth: 'none',
                              width: '100%',
                              minWidth: 0,
                              paddingLeft: 0
                          }
                      } : {})
                  }}>
                {subItems.map((child: any, index: number) =>
                    renderGridSubItem(child, `child-${index}`, router)
                )}
            </Grid>
        </CCOContainer>
    }

    function getActiveItem(items: any[] | undefined, path: string) {
        if (!items || items.length === 0) {
            return undefined;
        }
        const activeItem = items.find((item: any) => isActive(item, router));

        return activeItem ? activeItem : path.split('/').length <= 2 ? props.navigationRoot : undefined;
    }

    return <>
        <CCOContainer width={"full"} gridContainerMaxWidth={gridContainerMaxWidth}>
            <Grid id={"naviGrid"} container spacing={greaterThanWindowLarge ? 0 : 4} size={{xs: 12}}
                  sx={{
                      px: padding,
                      py: 4,
                      ...(greaterThanWindowLarge ? {
                          display: 'grid',
                          gridTemplateColumns: `repeat(${displayedItemsCount}, ${desktopGridColumnWidth})`,
                          columnGap: `${DESKTOP_TEASER_GAP}px`,
                          justifyContent: 'center',
                          '& > .MuiGrid-root': {
                              flexBasis: 'auto',
                              maxWidth: 'none',
                              width: '100%',
                              minWidth: 0,
                              paddingLeft: 0
                          }
                      } : {})
                  }}>
                {greaterThanWindowLarge ? <>
                    {
                        renderGridItem(props.navigationRoot, 'root', router)
                    }
                    {
                        children.map((child: any, index: number) => (
                            renderGridItem(child, `child-${index}`, router)))
                    }
                </> : activeMobileItem && renderGridItem(activeMobileItem, 'active-mobile', router, true)}
            </Grid>
        </CCOContainer>
        {
            renderSecondaryNavigation(activeItem, router)
        }
    </>
}

export default C091EasyLangContainer;